import { darken } from 'polished';
import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import useResizeObserver from 'use-resize-observer';
import { Colors, remInPx, toRem } from '.';
import { UserAgent } from '../platform/UserAgent';

export type DropButtonProps = {
    active?: boolean,
    inline?: boolean,
    overflow?: number | string,
    disabled?: boolean,
    padding?: string | number,
    rounding?: string | number,
    wrap?: boolean,
};

const BorderMount = styled.div``;

const Base = styled.div<DropButtonProps>`
    display: ${p => p.inline ? 'inline-flex' : 'flex'};
    vertical-align: top;
    align-items: center;
    justify-content: center;
    gap: .5rem;

    position: relative;
    cursor: pointer;
    user-select: none;
    ${p => p.padding ? `padding: ${toRem(p.padding)};` : ''}
    white-space: ${p => p.wrap ? 'normal' : 'nowrap'};
    
    ${UserAgent.browser.name !== 'Safari' && css`
        transition: padding 200ms cubic-bezier(0, .99, .2, 1.14);
    `};

    input {
        display: block;
        padding: 0;
        border: 0;
        background: transparent;
    }

    > * {
        position: relative;
    }

    &:before {
        content: '';
        position: absolute;
        inset: -${p => toRem(p.overflow || .25)};
        background: ${Colors.hover};
        border-radius: ${p => toRem(p.rounding === 'full' ? 10000 : (p.rounding || .5))};
        opacity: 0;
        transform: scale(0.8);
        pointer-events: none;
        
        ${UserAgent.browser.name !== 'Safari' && css`
            transition:
                opacity 200ms ease-out,
                inset 200ms cubic-bezier(0, .99, .2, 1.14),
                transform 200ms cubic-bezier(0, .99, .2, 1.14);
        `};
    }

    ${BorderMount} {
        position: absolute;
        inset: -${p => toRem(p.overflow || .25)};
    }

    &:active {
        &:before {
            background: ${darken(.05, Colors.hover)};
        }
    }

    ${p => !p.active ? '' : css`
        &:before {
            opacity  : 1;
            background: ${darken(.05, Colors.hover)};
            transform: scale(1);
        }
    `}

    &:hover, &:active {
        &:before {
            opacity  : 1;
            transform: scale(1);
            transition: none;
        }
    }

    ${p => p.disabled && css`
        cursor: initial;
        opacity: .5;
        filter: grayscale(50%);
        &:before {
            display: none;
        }
    `}
`;

const Border = ({ color }: { color?: string }) => {
    const ref = useRef<HTMLDivElement>();
    const { width, height } = useResizeObserver({ ref });
    const stroke = 2;
    const offset = stroke / 2;
    return <BorderMount ref={ref} >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${width || 0} ${height || 0}`}>
            <rect
                x={offset}
                y={offset}
                width={(width - stroke) || 0}
                height={(height - stroke) || 0}
                rx={remInPx(.5)}
                stroke={color || 'currentColor'}
                strokeWidth={2} fill="none" />
        </svg>
    </BorderMount>;
};

export const DropButton = Object.assign(Base, { Border });

export interface BetterDropButtonProps {
    variant?: 'primary' | 'default' | 'inverted';
    active?: boolean;
    inline?: boolean;
    overflow?: number | string;
    disabled?: boolean;
    padding?: string | number;
    rounding?: string | number;
}

export const BetterDropButton = styled.div<BetterDropButtonProps>`
    display: ${p => p.inline ? 'inline-flex' : 'flex'};
    vertical-align: top;
    align-items: center;
    position: relative;
    cursor: pointer;
    user-select: none;
    ${p => p.variant === 'primary' ? `color: ${Colors.primaryText};` : ''}
    ${p => p.variant === 'inverted' ? `color: ${Colors.hover};` : ''}
    ${p => p.padding ? `padding: ${toRem(p.padding)};` : ''}

    > * {
        position: relative;
    }

    &:before {
        content: '';
        position: absolute;
        inset: -${p => toRem(p.overflow || .25)};
        background: ${Colors.hover};
        border-radius: ${p => toRem(p.rounding === 'full' ? 10000 : (p.rounding || .5))};
        opacity: 0;
        transform: scale(0.8);
        transition:
            opacity 200ms ease-out,
            transform 200ms cubic-bezier(0, .99, .2, 1.14);
        pointer-events: none;
    }

    &:active {
        &:before {
            background: ${Colors.active};
        }
    }

    ${p => !p.active ? '' : css`
        &:before {
            opacity  : 1;
            background: ${Colors.active};
            transform: scale(1);
        }
        ${p.variant === 'inverted' ? `color: ${Colors.primaryText};` : ''}
    `}

    &:hover, &:active {
        &:before {
            opacity  : 1;
            transform: scale(1);
            transition: none;
        }
        ${p => p.variant === 'inverted' ? `color: ${Colors.primaryText};` : ''}
    }

    ${p => !p.disabled ? '' : css`
        cursor: initial;
        &:before {
            display: none;
        }
    `}

    span {
        margin-left: 0.5rem;
        margin-right: .25rem;
        font-weight: 400;
    }
`;
